import { useState, type JSX, type ReactNode } from "react";
import { openStripeUrlInNewTab, useCustomerBilling } from "../../../hooks/useCustomerBilling";
import { SlotCadencePicker } from "../billing/SlotCadencePicker";
import { defaultSlotCadence } from "../../../types/billing";
import styles from "./connect.module.css";

// IP-352: gates the integration connect CTA on the tenant's integration-slot
// allowance. When the tenant has a subscription and is out of integration slots,
// the connect CTA is REPLACED by an inline "buy an integration slot" upsell --
// connecting is also blocked server-side (the connect endpoint returns 402
// SLOT_LIMIT), so this just keeps the UI honest. Buying is pay-first: the button
// opens a hosted Stripe Checkout in a new tab, and the slot is granted by the
// webhook after payment (this tab re-fetches on focus and the CTA reappears).
export const SlotGate = ({
  tenantId,
  children,
}: {
  tenantId: string;
  children: ReactNode;
}): JSX.Element => {
  const { summary, startSlotCheckout } = useCustomerBilling(tenantId);
  const [busy, setBusy] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Slot cadence choice (IP-389); defaults to the plan's own cadence below.
  const [pickedCadence, setPickedCadence] = useState<string | null>(null);

  // The upsell requires a subscription to buy against; gating on hasSubscription
  // is safe because a customer can't use the platform without one. (If that ever
  // changes, a no-subscription out-of-slots tenant would need an upgrade prompt
  // here rather than falling through to the Connect button the server 402s.)
  const outOfSlots = summary?.hasSubscription === true && summary.slotsRemaining <= 0;
  if (!outOfSlots || summary === null) return <>{children}</>;

  const cadence = pickedCadence ?? defaultSlotCadence(summary.planInterval, summary.slotPrices);

  const buy = async (): Promise<void> => {
    setBusy(true);
    setErrorMsg(null);
    const result = await openStripeUrlInNewTab(() =>
      startSlotCheckout(window.location.pathname, cadence ?? undefined),
    );
    setBusy(false);
    if (!result.ok) setErrorMsg(result.message);
  };

  return (
    <div className={styles.connectBlock}>
      <span className={styles.ctaEyebrow}>// OUT OF INTEGRATION SLOTS</span>
      <p className={styles.ctaText}>
        You&apos;re using all <strong>{summary.slotAllowance}</strong> of your integration slots. Add
        one more to connect this integration.
      </p>
      <SlotCadencePicker
        prices={summary.slotPrices}
        value={cadence}
        onChange={setPickedCadence}
        disabled={busy}
      />
      <button type="button" className={styles.ctaButton} disabled={busy} onClick={() => void buy()}>
        Buy an integration slot
      </button>
      {errorMsg ? <p className={styles.error}>{errorMsg}</p> : null}
    </div>
  );
};
